/**
 * StatusUpdateComposer — Oppdatering composer.
 *
 * Mobile: start with two large buttons (Dikter | Tekst); hide editor until the user
 * picks Tekst or finishes dictation. Desktop keeps a compact always-available composer.
 * Visibility: team | admin | self. AI suggestions (todos/projects/users) auto-refresh
 * while typing (debounced). Todos update in the confirm list; project/user chips are
 * accepted or dismissed by the user (never silent-auto-tagged on each refresh).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  PaperAirplaneIcon,
  PlusIcon,
  UsersIcon,
  LockClosedIcon,
  EyeSlashIcon,
  XMarkIcon,
  MicrophoneIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  Bars3BottomLeftIcon,
} from "@heroicons/react/24/outline";
import { DocumentUploadSectionV4 } from "../Media/DocumentUploadSectionV4";
import { documentSrc } from "../documents/utils";
import { deleteDocument } from "../../../api/document";
import { useDocumentUploadQueueOptional } from "../../../contexts/document-upload-queue-context";
import { VoiceTranscriptButton } from "./VoiceTranscriptButton";
import { SuggestedTodosConfirm, todoKey } from "./SuggestedTodosConfirm";
import { SuggestedHoursConfirm, hourSuggestionKey } from "./SuggestedHoursConfirm";
import {
  MentionTextEditor,
  extractMentionedUsers,
  extractModuleTags,
} from "../../utils/MentionTextEditor";
import { TWclassNames } from "../../utils/Div";
import {
  createStatusUpdate,
  isStatusUpdateModuleUnavailable,
  suggestStatusUpdateContent,
} from "../../../api/statusUpdates";

const VISIBILITY_OPTIONS = [
  { value: "team", labelKey: "Alle med tilgang", Icon: UsersIcon },
  { value: "admin", labelKey: "Kun admin", Icon: LockClosedIcon },
  { value: "self", labelKey: "Kun meg", Icon: EyeSlashIcon },
];

/** Debounce after last body change before calling suggest API. */
const SUGGEST_DEBOUNCE_MS = 600;
/** Minimum plain-text length before requesting suggestions. */
const SUGGEST_MIN_LENGTH = 8;

const MOBILE_MQ = "(max-width: 767px)";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

function stripHtml(html) {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function suggestionContextKey(ctx) {
  if (!ctx || typeof ctx !== "object") return "";
  return [
    ctx.subject_type || "",
    ctx.subject_id || "",
    ctx.project_id || "",
    ctx.customer_id || "",
  ].join("|");
}

function buildSuggestionContext({
  subjectType,
  subjectId,
  projectId,
  project,
  customer,
}) {
  const ctx = {};
  if (subjectType) ctx.subject_type = subjectType;
  if (subjectId) ctx.subject_id = subjectId;
  const pid =
    subjectType === "project" ? subjectId : projectId || project?.id || null;
  if (pid) ctx.project_id = pid;
  const pname = project?.displayTitle || project?.name || "";
  if (pname) ctx.project_name = pname;
  const cid = subjectType === "customer" ? subjectId : customer?.id || null;
  if (cid) ctx.customer_id = cid;
  const cname = customer?.displayTitle || customer?.name || "";
  if (cname) ctx.customer_name = cname;
  return ctx;
}

export function StatusUpdateComposer({
  subjectType,
  subjectId,
  onCreated,
  customer,
  /** Parent project id when subject is work_order / project_activity (for todo suggestions). */
  projectId = null,
  /** Optional project object for hour-suggestion defaults (name). */
  project = null,
  disabled = false,
  compact = false,
  iconToolbar = false,
  /** Optional slot rendered in the toolbar (e.g. voice/transcript button). */
  toolbarSlot = null,
  /** Home composer: bind project subject when user accepts an AI project chip. */
  onAcceptSuggestedProject = null,
  /** Called when StatusUpdate API routes look undeployed/missing. */
  onModuleUnavailable = null,
}) {
  const { t } = useTranslation();
  const uploadQueue = useDocumentUploadQueueOptional();
  const [stagedAttachments, setStagedAttachments] = useState([]);
  const stagedAttachmentsRef = useRef([]);
  const submitRef = useRef(false);
  useEffect(() => () => {
    stagedAttachmentsRef.current.filter((entry) => !entry.queued).forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
  }, []);
  const stageAttachments = (files) => {
    const selected = files.map((entry) => ({ ...entry, key: `${Date.now()}-${Math.random()}` }));
    stagedAttachmentsRef.current = [...stagedAttachmentsRef.current, ...selected];
    setStagedAttachments(stagedAttachmentsRef.current);
  };
  const isMobile = useIsMobile();
  const projectName = project?.displayTitle || project?.name || "";
  const customerName = customer?.displayTitle || customer?.name || "";
  const suggestionContext = useMemo(
    () =>
      buildSuggestionContext({
        subjectType,
        subjectId,
        projectId,
        project,
        customer,
      }),
    // Primitive deps: HomeStatusComposer passes a new `project` object each render.
    [subjectType, subjectId, projectId, project?.id, projectName, customer?.id, customerName]
  );
  // Mobile entry: null = choose Dikter|Tekst; "recording" = dictate in progress (no editor);
  // "text" | "dictate" = show editor (dictate = after transcript arrives).
  // Desktop always shows editor.
  const [entryMode, setEntryMode] = useState(null);
  const [showTitle, setShowTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [insertMentionRequest, setInsertMentionRequest] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [tags, setTags] = useState([]);
  const [visibility, setVisibility] = useState("team");
  const [source, setSource] = useState("typed");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestedTodos, setSuggestedTodos] = useState([]);
  const [suggestedHours, setSuggestedHours] = useState([]);
  const [suggestedProjects, setSuggestedProjects] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  /**
   * Todos the user already created this session: `{ title, taskId?, status: 'created' }`.
   * Shown as «Opprettet»; never re-queued as Opprett. Synced via onCreated + suggest overlap.
   */
  const [createdSuggestions, setCreatedSuggestions] = useState([]);
  const [createdHourSuggestions, setCreatedHourSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  /** Keys (`project:id` / `user:id`) the user dismissed — survive debounce refreshes. */
  const [dismissedSuggestionKeys, setDismissedSuggestionKeys] = useState(
    () => new Set()
  );
  const [lastCreatedId, setLastCreatedId] = useState(null);
  /** Plain body of last successful Publiser — used to keep composer + block duplicate publish.
   *  Empty string is valid (attachment-/title-only publish). */
  const [publishedPlain, setPublishedPlain] = useState(null);
  const [autoDictate, setAutoDictate] = useState(false);

  // Stale-response guard for suggest API (no AbortSignal on helper).
  const suggestGenRef = useRef(0);
  const lastFetchedPlainRef = useRef("");
  const lastFetchedContextRef = useRef("");
  /**
   * Normalized titles dismissed via «Hopp over» this session.
   * Created titles live in acceptedTodoTitlesRef — not here.
   */
  const dismissedTodoTitlesRef = useRef(new Set());
  /** Mirror of createdSuggestions for sync reads inside applySuggestions / fetch. */
  const createdSuggestionsRef = useRef([]);
  /**
   * Titles the user created this session (normalized → display title).
   * Survives «Lukk» on the Opprettet panel so AI does not re-suggest them;
   * cleared only by «Ny oppdatering» / full reset.
   */
  const acceptedTodoTitlesRef = useRef(new Map());
  const dismissedHourKeysRef = useRef(new Set());
  const acceptedHourKeysRef = useRef(new Set());
  const createdHourSuggestionsRef = useRef([]);
  /** True once the user manually edits the title — AI must not overwrite it. */
  const titleTouchedRef = useRef(false);
  /**
   * Task IDs already attached to a published StatusUpdate (via linked_task_ids or
   * post-publish Opprett). Excluded from the next Publiser payload so a body-edit
   * "new draft" does not re-tag prior Opprettet todos onto a new post.
   */
  const alreadyLinkedTaskIdsRef = useRef(new Set());
  /** Mirror of lastCreatedId for sync reads in todo-created handler. */
  const lastCreatedIdRef = useRef(null);

  // The dashboard exposes dictation in its compact toolbar on every viewport.
  // Other placements retain the two-step mobile dictate/text chooser.
  const showEditor = iconToolbar || !isMobile || entryMode === "text" || entryMode === "dictate";
  const showDictateOnly = !iconToolbar && isMobile && entryMode === "recording";

  // Publish gate: plain body text, attachments, or title. Subject is optional.
  // Never gated on suggestionsLoading / leftover suggestedTodos (confirm-only UX).
  // After Publiser, disable until the user starts a new draft (Ny oppdatering)
  // or edits the body away from the published snapshot.
  // publishedPlain is null before first publish; "" is a valid attachment-only snapshot.
  const plainBody = stripHtml(body);
  const hasContent =
    plainBody.length > 0 || (documents?.length > 0) || stagedAttachments.length > 0 || !!title?.trim();
  const alreadyPublished =
    lastCreatedId != null &&
    publishedPlain !== null &&
    plainBody === publishedPlain;
  const canSubmit = hasContent && !disabled && !isSubmitting && !alreadyPublished;

  /** Pending = not accepted (created) and not dismissed. Key includes place. */
  const filterPendingTodos = useCallback((todos) => {
    const dismissed = dismissedTodoTitlesRef.current;
    const accepted = acceptedTodoTitlesRef.current;
    return (Array.isArray(todos) ? todos : []).filter((todo) => {
      const key = todoKey(todo);
      return key && !dismissed.has(key) && !accepted.has(key);
    });
  }, []);

  const dismissPendingTodos = useCallback((todosOrTitles) => {
    const dismissed = dismissedTodoTitlesRef.current;
    (todosOrTitles || []).forEach((item) => {
      const key = typeof item === "string" ? todoKey({ title: item }) : todoKey(item);
      if (key) dismissed.add(key);
    });
  }, []);

  /** Append/merge created rows; overlapping normalized titles stay created (with taskId). */
  const rememberCreatedSuggestions = useCallback((rows) => {
    const incoming = (rows || [])
      .map((item) => {
        if (typeof item === "string") {
          const title = String(item).trim();
          return title ? { title, taskId: null, status: "created" } : null;
        }
        const title = String(item?.title || "").trim();
        if (!title) return null;
        return {
          title,
          taskId: item?.taskId ?? null,
          status: "created",
          project_id: item?.project_id ?? null,
          customer_id: item?.customer_id ?? null,
        };
      })
      .filter(Boolean);
    if (!incoming.length) return;

    incoming.forEach((row) => {
      const key = todoKey(row);
      if (key) acceptedTodoTitlesRef.current.set(key, row.title);
    });

    setCreatedSuggestions((prev) => {
      const byKey = new Map(
        (prev || []).map((s) => [todoKey(s), s])
      );
      incoming.forEach((row) => {
        const key = todoKey(row);
        if (!key) return;
        const existing = byKey.get(key);
        byKey.set(key, {
          title: row.title,
          taskId: row.taskId ?? existing?.taskId ?? null,
          status: "created",
          project_id: row.project_id ?? existing?.project_id ?? null,
          customer_id: row.customer_id ?? existing?.customer_id ?? null,
        });
      });
      const next = Array.from(byKey.values());
      createdSuggestionsRef.current = next;
      return next;
    });
  }, []);

  const filterPendingHours = useCallback((rows) => {
    const dismissed = dismissedHourKeysRef.current;
    const accepted = acceptedHourKeysRef.current;
    return (Array.isArray(rows) ? rows : []).filter((hour) => {
      const key = hourSuggestionKey(hour);
      return key && !dismissed.has(key) && !accepted.has(key);
    });
  }, []);

  const dismissPendingHours = useCallback((rows) => {
    const dismissed = dismissedHourKeysRef.current;
    (rows || []).forEach((hour) => {
      const key = hourSuggestionKey(hour);
      if (key) dismissed.add(key);
    });
  }, []);

  const rememberCreatedHours = useCallback((rows) => {
    const incoming = (rows || []).filter(Boolean);
    if (!incoming.length) return;
    incoming.forEach((row) => {
      const key = hourSuggestionKey(row);
      if (key) acceptedHourKeysRef.current.add(key);
    });
    setCreatedHourSuggestions((prev) => {
      const next = [...(prev || []), ...incoming];
      createdHourSuggestionsRef.current = next;
      return next;
    });
  }, []);

  const applyAiTitle = useCallback((suggestedTitle) => {
    const next = String(suggestedTitle || "").trim();
    if (!next || titleTouchedRef.current) return;
    setTitle(next);
    setShowTitle(true);
  }, []);

  const applySuggestions = useCallback(
    (suggestions) => {
      if (!suggestions) return;
      const todos = Array.isArray(suggestions.todos) ? suggestions.todos : [];
      const hourRows = Array.isArray(suggestions.hours) ? suggestions.hours : [];
      const projects = Array.isArray(suggestions.projects)
        ? suggestions.projects
        : [];
      const users = Array.isArray(suggestions.users) ? suggestions.users : [];
      // Todos + project/user chips update live; tags are never silent-auto-applied.
      // Overlapping accepted titles stay Opprettet (if still in UI); only new go pending.
      const accepted = acceptedTodoTitlesRef.current;
      if (accepted.size) {
        const overlap = todos.filter((todo) => accepted.has(todoKey(todo)));
        // Re-attach taskId/title into the visible Opprettet list only when it is open.
        if (overlap.length && createdSuggestionsRef.current?.length) {
          rememberCreatedSuggestions(overlap);
        }
      }
      setSuggestedTodos(filterPendingTodos(todos));
      setSuggestedHours(filterPendingHours(hourRows));
      setSuggestedProjects(projects);
      setSuggestedUsers(users);
      // Refresh AI title when body changes, unless the user edited the title.
      if (suggestions.title) applyAiTitle(suggestions.title);
    },
    [applyAiTitle, filterPendingTodos, filterPendingHours, rememberCreatedSuggestions]
  );
  const tagKeyset = new Set(
    (tags || []).map((tg) => `${tg.module}:${tg.module_param}`)
  );

  const acceptSuggestedProject = (p) => {
    if (!p?.id) return;
    const key = `project:${p.id}`;
    setTags((prev) => {
      if (prev.some((tg) => `${tg.module}:${tg.module_param}` === key)) return prev;
      return [...prev, { module: "project", module_param: String(p.id) }];
    });
    onAcceptSuggestedProject?.(p);
  };

  const acceptSuggestedUser = (u) => {
    if (!u?.id) return;
    const key = `user:${u.id}`;
    setTags((prev) => {
      if (prev.some((tg) => `${tg.module}:${tg.module_param}` === key)) return prev;
      return [...prev, { module: "user", module_param: String(u.id) }];
    });
  };

  const dismissSuggestion = (key) => {
    if (!key) return;
    setDismissedSuggestionKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const visibleSuggestedProjects = suggestedProjects.filter((p) => {
    if (!p?.id) return true;
    const key = `project:${p.id}`;
    return !tagKeyset.has(key) && !dismissedSuggestionKeys.has(key);
  });
  const visibleSuggestedUsers = suggestedUsers.filter((u) => {
    if (!u?.id) return true;
    const key = `user:${u.id}`;
    return !tagKeyset.has(key) && !dismissedSuggestionKeys.has(key);
  });

  const clearSuggestionLists = useCallback(({ keepTodos = false, keepHours = false } = {}) => {
    if (!keepTodos) setSuggestedTodos([]);
    if (!keepHours) setSuggestedHours([]);
    setSuggestedProjects([]);
    setSuggestedUsers([]);
  }, []);

  const fetchSuggestionsForText = useCallback(
    async (plainOrHtml, { force = false, skipApply = false } = {}) => {
      const plain = stripHtml(plainOrHtml);
      const ctxKey = suggestionContextKey(suggestionContext);
      if (plain.length < SUGGEST_MIN_LENGTH) {
        clearSuggestionLists();
        lastFetchedPlainRef.current = "";
        lastFetchedContextRef.current = "";
        return null;
      }
      if (
        !force &&
        plain === lastFetchedPlainRef.current &&
        ctxKey === lastFetchedContextRef.current
      ) {
        return null;
      }

      const gen = ++suggestGenRef.current;
      setSuggestionsLoading(true);
      try {
        const acceptedTodoTitles = Array.from(
          acceptedTodoTitlesRef.current.values()
        );
        const acceptedHourKeys = Array.from(acceptedHourKeysRef.current);
        const res = await suggestStatusUpdateContent(plain, {
          acceptedTodoTitles,
          acceptedHourKeys,
          context: suggestionContext,
        });
        if (gen !== suggestGenRef.current) return null; // stale
        if (res && !res.__error && !res.errors) {
          lastFetchedPlainRef.current = plain;
          lastFetchedContextRef.current = ctxKey;
          if (!skipApply) applySuggestions(res);
          return res;
        }
        // Soft-fail: keep last good suggestions; no toast.
        return null;
      } catch {
        // Soft-fail silently — do not toast on every keystroke.
        return null;
      } finally {
        if (gen === suggestGenRef.current) {
          setSuggestionsLoading(false);
        }
      }
    },
    [applySuggestions, clearSuggestionLists, suggestionContext]
  );

  // Homepage can switch project/customer without changing the body. Drop stale
  // pending todos immediately so Opprett cannot attach them to the old place.
  const contextKey = suggestionContextKey(suggestionContext);
  const prevContextKeyRef = useRef(contextKey);
  useEffect(() => {
    if (prevContextKeyRef.current === contextKey) return;
    prevContextKeyRef.current = contextKey;
    suggestGenRef.current += 1;
    lastFetchedPlainRef.current = "";
    lastFetchedContextRef.current = "";
    setSuggestedTodos([]);
    setSuggestionsLoading(false);
  }, [contextKey]);

  // Debounced auto-suggestions as the user types / pastes / edits after dictate.
  useEffect(() => {
    const plain = stripHtml(body);

    // Body changed away from the published snapshot → treat as a new draft.
    // Keep lastCreatedId while the published text is unchanged (so late todo
    // creates can still link to the post without wiping the composer).
    // publishedPlain may be "" (attachment-/title-only); only null means "not published".
    if (
      lastCreatedId != null &&
      publishedPlain !== null &&
      plain !== publishedPlain
    ) {
      lastCreatedIdRef.current = null;
      setLastCreatedId(null);
      setPublishedPlain(null);
    }

    if (plain.length < SUGGEST_MIN_LENGTH) {
      suggestGenRef.current += 1;
      setSuggestionsLoading(false);
      lastFetchedPlainRef.current = "";
      lastFetchedContextRef.current = "";
      // Keep todo/hour lists (pending + Opprettet); only drop project/user chips.
      clearSuggestionLists({ keepTodos: true, keepHours: true });
      return undefined;
    }

    const timer = setTimeout(() => {
      fetchSuggestionsForText(body);
    }, SUGGEST_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    body,
    fetchSuggestionsForText,
    clearSuggestionLists,
    lastCreatedId,
    publishedPlain,
  ]);

  const handleUploadCallback = (uploadedDocs) => {
    const merged = [...documents, ...(uploadedDocs || [])].filter(
      (v, i, a) => a.findIndex((d) => d.id === v.id) === i
    );
    setDocuments(merged);
  };

  const handleRemoveDocument = async (doc) => {
    if (!doc?.id) return;
    setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    try {
      await deleteDocument(doc.id);
    } catch (e) {
      /* orphan draft doc is harmless */
    }
  };

  /**
   * Insert a dictation into the composer. Opens the editor on mobile after dictate.
   * Applies meta.suggestions when present; further edits trigger debounced auto-suggest.
   */
  const appendTranscript = (text, meta = {}) => {
    const bodyHtml = meta?.bodyHtml;
    const suggestedTitle = meta?.title;
    if (!text && !bodyHtml) return;
    setSource("transcript");
    setEntryMode("dictate");
    setAutoDictate(false);

    const clean = body && body !== "<p><br></p>" ? body : "";
    let nextBody;
    if (bodyHtml && bodyHtml.trim()) {
      nextBody = `${clean}${bodyHtml}`;
    } else {
      const safe = String(text).replace(/</g, "&lt;").replace(/>/g, "&gt;");
      nextBody = `${clean}<p>${safe}</p>`;
    }
    setBody(nextBody);

    if (suggestedTitle && suggestedTitle.trim()) {
      applyAiTitle(suggestedTitle.trim());
    }

    if (meta?.suggestions) {
      applySuggestions(meta.suggestions);
      const plain = stripHtml(nextBody);
      if (plain.length >= SUGGEST_MIN_LENGTH) {
        // Skip identical debounce refetch right after transcription suggestions.
        lastFetchedPlainRef.current = plain;
        lastFetchedContextRef.current = suggestionContextKey(suggestionContext);
      }
    }
    // If no meta.suggestions, debounced body effect fetches after setBody.
  };

  const resetComposerFields = () => {
    stagedAttachmentsRef.current.filter((entry) => !entry.queued).forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
    stagedAttachmentsRef.current = [];
    setStagedAttachments([]);
    suggestGenRef.current += 1;
    lastFetchedPlainRef.current = "";
    lastFetchedContextRef.current = "";
    dismissedTodoTitlesRef.current = new Set();
    dismissedHourKeysRef.current = new Set();
    acceptedHourKeysRef.current = new Set();
    createdHourSuggestionsRef.current = [];
    setCreatedHourSuggestions([]);
    setSuggestedHours([]);
    createdSuggestionsRef.current = [];
    acceptedTodoTitlesRef.current = new Map();
    titleTouchedRef.current = false;
    alreadyLinkedTaskIdsRef.current = new Set();
    lastCreatedIdRef.current = null;
    setCreatedSuggestions([]);
    setSuggestionsLoading(false);
    setSuggestedTodos([]);
    setSuggestedProjects([]);
    setSuggestedUsers([]);
    setDismissedSuggestionKeys(new Set());
    setLastCreatedId(null);
    setPublishedPlain(null);
    setBody("");
    setTitle("");
    setDocuments([]);
    setTags([]);
    setVisibility("team");
    setSource("typed");
    setShowTitle(false);
    setEntryMode(isMobile ? null : "text");
    setAutoDictate(false);
  };

  const handleSuggestedTodosCreated = useCallback(
    (_createdTasks, createdRows) => {
      // createdRows include `{ title, taskId, status: 'created' }` from SuggestedTodosConfirm
      rememberCreatedSuggestions(createdRows);
      // Post-publish Opprett already tags status_update — do not re-send on next Publiser.
      if (lastCreatedIdRef.current != null) {
        (createdRows || []).forEach((row) => {
          const id = Number(row?.taskId);
          if (id > 0) alreadyLinkedTaskIdsRef.current.add(id);
        });
      }
      const plain = stripHtml(body);
      if (plain.length >= SUGGEST_MIN_LENGTH) {
        // Force next suggest call so accepted_todo_titles is sent immediately.
        lastFetchedPlainRef.current = "";
        fetchSuggestionsForText(body, { force: true });
      }
      const createdKeys = new Set(
        (createdRows || []).map((row) => todoKey(row)).filter(Boolean)
      );
      setSuggestedTodos((prev) =>
        (prev || []).filter((todo) => {
          const key = todoKey(todo);
          return (
            key &&
            !createdKeys.has(key) &&
            !dismissedTodoTitlesRef.current.has(key)
          );
        })
      );
    },
    [body, fetchSuggestionsForText, rememberCreatedSuggestions]
  );

  const handleSuggestedTodosDismiss = useCallback(() => {
    // Hopp over: dismiss pending only. Lukk (no pending): hide Opprettet panel.
    // Keep acceptedTodoTitlesRef so AI does not re-suggest created titles after Lukk.
    if (suggestedTodos.length > 0) {
      dismissPendingTodos(suggestedTodos);
      const plain = stripHtml(body);
      if (plain.length >= SUGGEST_MIN_LENGTH) {
        lastFetchedPlainRef.current = plain;
        lastFetchedContextRef.current = suggestionContextKey(suggestionContext);
      }
      setSuggestedTodos([]);
      return;
    }
    createdSuggestionsRef.current = [];
    setCreatedSuggestions([]);
  }, [body, dismissPendingTodos, suggestedTodos, suggestionContext]);

  const handleSuggestedHoursCreated = useCallback((createdRows) => {
    rememberCreatedHours(createdRows);
    const createdKeys = new Set(
      (createdRows || []).map((row) => hourSuggestionKey(row)).filter(Boolean)
    );
    setSuggestedHours((prev) =>
      (prev || []).filter((hour) => {
        const key = hourSuggestionKey(hour);
        return key && !createdKeys.has(key) && !dismissedHourKeysRef.current.has(key);
      })
    );
  }, [rememberCreatedHours]);

  const handleSuggestedHoursDismiss = useCallback(() => {
    if (suggestedHours.length > 0) {
      dismissPendingHours(suggestedHours);
      setSuggestedHours([]);
      return;
    }
    createdHourSuggestionsRef.current = [];
    setCreatedHourSuggestions([]);
  }, [dismissPendingHours, suggestedHours]);

  const handleSubmit = async () => {
    if (!canSubmit || submitRef.current) return;
    submitRef.current = true;

    let bodyHtml = body?.trim() || "";
    // Treat Quill empty shells (`<p><br></p>`, `<p></p>`, nbsp-only) as empty.
    if (!stripHtml(bodyHtml)) bodyHtml = "";

    const mentionedUsers = extractMentionedUsers(body || "");
    const userTags = mentionedUsers.map((u) => ({ module: "user", module_param: u.id }));
    const slashTags = extractModuleTags(body || "");
    const tagKeys = new Set(tags.map((tg) => `${tg.module}:${tg.module_param}`));
    const mergedSlashTags = slashTags.filter(
      (tg) => !tagKeys.has(`${tg.module}:${tg.module_param}`)
    );
    const allTags = [...tags, ...mergedSlashTags, ...userTags]
      .map((tg) => ({
        module: String(tg.module || ""),
        module_param: tg.module_param != null ? String(tg.module_param) : "",
      }))
      .filter((tg) => tg.module && tg.module_param);

    const notifyEmployees =
      mentionedUsers.length > 0 ? mentionedUsers.map((u) => ({ id: u.id })) : undefined;

    // Tasks created before Publiser are attached via linked_task_ids (API
    // find_or_create status_update module_tag — does not wipe other tags).
    // Skip IDs already linked to a prior publish / post-publish Opprett.
    const linkedTaskIds = (createdSuggestionsRef.current || [])
      .map((s) => s?.taskId)
      .filter((id) => id != null && Number(id) > 0)
      .filter((id) => !alreadyLinkedTaskIdsRef.current.has(Number(id)));

    const payload = {
      title: title?.trim() || undefined,
      body_html: bodyHtml || (stagedAttachments.length ? `<p>${t("statusAttachments.attachmentPost")}</p>` : " "),
      visibility,
      source,
      documents: documents.map((d) => ({ id: d.id })),
      tags: allTags.length > 0 ? allTags : undefined,
      notifyEmployees,
      linked_task_ids: linkedTaskIds.length ? linkedTaskIds : undefined,
    };
    if (subjectType && subjectId) {
      payload.subject_type = subjectType;
      payload.subject_id = subjectId;
    }

    setIsSubmitting(true);
    try {
      const res = await createStatusUpdate(payload);
      if (isStatusUpdateModuleUnavailable(res)) {
        onModuleUnavailable?.(res);
        toast.error(t("status_update.unavailable_title"));
        return;
      }
      if (res && res.id) {
        // Keep composer intact after Publiser — body, title, todos, chips, attachments.
        linkedTaskIds.forEach((id) =>
          alreadyLinkedTaskIdsRef.current.add(Number(id))
        );
        lastCreatedIdRef.current = res.id;
        setLastCreatedId(res.id);
        setPublishedPlain(stripHtml(bodyHtml));
        const photos = stagedAttachmentsRef.current.filter((entry) => !entry.queued);
        if (photos.length && uploadQueue) {
          photos.forEach((entry) => { entry.queued = true; });
          uploadQueue.enqueueUpload({
            files: photos.map((entry) => ({ ...entry, module: "status_update", moduleParam: res.id })),
            module: "status_update", moduleParam: res.id, destinationLabel: t("statusPhotos.destination", { id: res.id }),
            onComplete: (uploaded) => {
              if (lastCreatedIdRef.current === res.id) {
                setDocuments((previous) => [...previous, ...uploaded]);
                stagedAttachmentsRef.current = stagedAttachmentsRef.current.filter((entry) => !photos.includes(entry));
                setStagedAttachments(stagedAttachmentsRef.current);
              }
            },
          });
        }
        toast.success(t("Publisert"));
        try { onCreated?.(res); } catch (_) { /* Post is confirmed; keep its id even if the host refresh fails. */ }
      } else {
        const message =
          res?.errors?.[0] ||
          res?.error ||
          res?.message ||
          t("Kunne ikke publisere oppdateringen");
        toast.error(message);
      }
    } catch (e) {
      if (isStatusUpdateModuleUnavailable(e)) {
        onModuleUnavailable?.(e);
        toast.error(t("status_update.unavailable_title"));
      } else {
        toast.error(t("Kunne ikke publisere oppdateringen"));
      }
    } finally {
      submitRef.current = false;
      setIsSubmitting(false);
    }
  };
  const chooseText = () => {
    setEntryMode("text");
    setSource("typed");
  };

  const chooseDictate = () => {
    // Keep editor hidden until transcript arrives (appendTranscript → "dictate").
    setEntryMode("recording");
    setAutoDictate(true);
  };

  const visibilityChoices = (
    <div className={iconToolbar ? "pd-visibility-choices" : "ml-1 inline-flex overflow-hidden rounded-full border border-slate-200 bg-slate-50"}>
      {VISIBILITY_OPTIONS.map(({ value, labelKey, Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => setVisibility(value)}
          className={TWclassNames(
            iconToolbar ? "pd-visibility-choice" : "flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors",
            !iconToolbar && (visibility === value ? "bg-[#1463C7] text-white" : "text-slate-600 hover:bg-slate-100")
          )}
          title={t(labelKey)}
          aria-label={t(labelKey)}
          aria-pressed={visibility === value}
        >
          <Icon className="h-4 w-4" />
          <span className={iconToolbar ? "sr-only" : "hidden sm:inline"}>{t(labelKey)}</span>
        </button>
      ))}
    </div>
  );

  // Mobile: dictate in progress — mic only, no textarea yet
  if (showDictateOnly) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700">{t("Dikter oppdatering")}</p>
          <button
            type="button"
            onClick={() => {
              setEntryMode(null);
              setAutoDictate(false);
            }}
            className="text-xs font-medium text-[#1463C7] hover:underline"
          >
            {t("Bytt metode")}
          </button>
        </div>
        <div className="flex flex-col items-center gap-3 py-6">
          <VoiceTranscriptButton
            onTranscript={appendTranscript}
            disabled={disabled}
            autoStart={autoDictate}
            onAutoStartConsumed={() => setAutoDictate(false)}
            suggestContext={suggestionContext}
          />
          <p className="text-center text-xs text-slate-500">
            {t("Trykk for å stoppe opptaket. Teksten vises etterpå.")}
          </p>
        </div>
      </div>
    );
  }

  // Mobile chooser — two large buttons (editor hidden)
  if (isMobile && !showEditor) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]">
        <p className="mb-3 text-sm text-slate-600">
          {t("Hvordan vil du legge inn oppdateringen?")}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={chooseDictate}
            disabled={disabled}
            className="flex min-h-[112px] flex-col items-center justify-center gap-2 rounded-2xl border border-[#BFDCF7] bg-[linear-gradient(160deg,#EAF3FD,#F2F8FE)] px-3 py-4 text-[#0B3A78] transition-transform active:scale-[0.98]"
          >
            <MicrophoneIcon className="h-8 w-8 text-[#1463C7]" />
            <span className="text-base font-bold">{t("Dikter")}</span>
          </button>
          <button
            type="button"
            onClick={chooseText}
            disabled={disabled}
            className="flex min-h-[112px] flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-4 text-slate-800 transition-transform active:scale-[0.98] hover:bg-slate-50"
          >
            <PencilSquareIcon className="h-8 w-8 text-[#1463C7]" />
            <span className="text-base font-bold">{t("Tekst")}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={TWclassNames("flex flex-col gap-0", iconToolbar && "pd-status-compose")}>
      <div
        className={TWclassNames(
          iconToolbar ? "pd-status-compose-inner" : "rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]",
          compact && "text-sm"
        )}
      >
        {isMobile && !iconToolbar && (
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-xs font-medium text-slate-500">
              {entryMode === "dictate" ? t("Dikter") : t("Tekst")}
            </span>
            <button
              type="button"
              onClick={() => {
                setEntryMode(null);
                setAutoDictate(false);
              }}
              className="text-xs font-medium text-[#1463C7] hover:underline"
            >
              {t("Bytt metode")}
            </button>
          </div>
        )}

        {showTitle && (
          <div className="border-b border-slate-100 px-4 pt-3">
            <input
              type="text"
              value={title}
              onChange={(e) => {
                titleTouchedRef.current = true;
                setTitle(e.target.value);
              }}
              placeholder={t("Tittel (valgfritt)")}
              className="w-full rounded-lg border-0 bg-transparent py-1 text-[15px] font-semibold text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-0"
            />
          </div>
        )}

        <div className={iconToolbar ? "pd-status-editor" : "px-4 pt-3"}>
          <MentionTextEditor
            variant="innlegg"
            className={iconToolbar ? "pd-mention-editor" : undefined}
            value={body}
            onChange={setBody}
            insertMentionRequest={insertMentionRequest}
            placeholder={t(iconToolbar ? "dashboardDesign.updatePrompt" : "Skriv en oppdatering… bruk @ for å nevne, / for å koble modul")}
            minHeight={compact ? 72 : 96}
            maxHeight={compact ? 140 : 200}
            disabled={disabled}
            enableSlashCommands={true}
            onTagSelect={(tag) => setTags((prev) => [...prev, tag])}
            customer={customer}
          />
        </div>

        {(suggestionsLoading ||
          visibleSuggestedProjects.length > 0 ||
          visibleSuggestedUsers.length > 0) && (
          <div className="border-t border-slate-100 px-4 py-2">
            <p className="mb-1.5 text-xs font-medium text-slate-500">
              {suggestionsLoading ? t("Tolker…") : t("AI-forslag")}
            </p>
            {(visibleSuggestedProjects.length > 0 ||
              visibleSuggestedUsers.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {visibleSuggestedProjects.map((p, i) => {
                  const key = p?.id ? `project:${p.id}` : null;
                  const label = p.name || (p.id ? `#${p.id}` : "…");
                  if (!p?.id) {
                    return (
                      <span
                        key={`p-${p.name || i}`}
                        className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800"
                      >
                        {label}
                      </span>
                    );
                  }
                  return (
                    <span
                      key={`p-${p.id}-${i}`}
                      className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-emerald-800"
                    >
                      <button
                        type="button"
                        onClick={() => acceptSuggestedProject(p)}
                        title={t("oppdatering.suggestion_add")}
                        className="hover:underline"
                      >
                        {label}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissSuggestion(key)}
                        title={t("oppdatering.suggestion_dismiss")}
                        aria-label={t("oppdatering.suggestion_dismiss")}
                        className="rounded-full p-0.5 text-emerald-700/70 hover:bg-emerald-100 hover:text-emerald-900"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  );
                })}
                {visibleSuggestedUsers.map((u, i) => {
                  const key = u?.id ? `user:${u.id}` : null;
                  const label = `@${u.name || u.id || "…"}`;
                  if (!u?.id) {
                    return (
                      <span
                        key={`u-${u.name || i}`}
                        className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800"
                      >
                        {label}
                      </span>
                    );
                  }
                  return (
                    <span
                      key={`u-${u.id}-${i}`}
                      className="inline-flex items-center gap-0.5 rounded-full bg-sky-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-sky-800"
                    >
                      <button
                        type="button"
                        onClick={() => acceptSuggestedUser(u)}
                        title={t("oppdatering.suggestion_add")}
                        className="hover:underline"
                      >
                        {label}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissSuggestion(key)}
                        title={t("oppdatering.suggestion_dismiss")}
                        aria-label={t("oppdatering.suggestion_dismiss")}
                        className="rounded-full p-0.5 text-sky-700/70 hover:bg-sky-100 hover:text-sky-900"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {stagedAttachments.length > 0 && <div className="flex flex-wrap gap-2 border-t px-4 py-2">
          {stagedAttachments.map((entry) => <div key={entry.key} className="relative">
            {entry.file.type.startsWith("image/") ? <img src={entry.previewUrl} alt={entry.title} className="h-20 w-20 rounded object-cover" /> : <div className="flex h-20 w-28 flex-col items-center justify-center gap-1 rounded bg-slate-100 p-2 text-slate-600"><DocumentTextIcon className="h-6 w-6 shrink-0" aria-hidden="true" /><span className="line-clamp-2 break-all text-xs">{entry.title || entry.file.name}</span></div>}
            {!entry.queued && <button type="button" disabled={isSubmitting} aria-label={t("Fjern vedlegg")} className="absolute right-0 top-0 rounded bg-black px-1 text-white" onClick={() => {
              URL.revokeObjectURL(entry.previewUrl);
              stagedAttachmentsRef.current = stagedAttachmentsRef.current.filter((photo) => photo.key !== entry.key);
              setStagedAttachments(stagedAttachmentsRef.current);
            }}>×</button>}
          </div>)}
          <p className="w-full text-xs text-slate-600">{t("statusAttachments.backgroundHint")}</p>
        </div>}
        {documents?.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2">
            <div className="flex flex-wrap gap-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="group relative h-16 w-16 overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                >
                  <img
                    src={documentSrc(doc, "thumb")}
                    alt={doc?.file?.filename || doc?.title || ""}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveDocument(doc)}
                    title={t("Fjern vedlegg")}
                    aria-label={t("Fjern vedlegg")}
                    className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100 focus:opacity-100"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={iconToolbar ? "pd-status-toolbar" : "flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-3 py-2"}>
          <div className={iconToolbar ? "pd-status-tools" : "flex flex-wrap items-center gap-1"}>
            <button
              type="button"
              onClick={() => setShowTitle(!showTitle)}
              className={TWclassNames(
                iconToolbar ? "pd-composer-tool" : "flex items-center gap-1 rounded-lg px-2 py-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700",
                showTitle && "bg-slate-100 text-[#1463C7]"
              )}
              title={t("Legg til tittel")}
              aria-label={t("Legg til tittel")}
              aria-pressed={showTitle}
            >
              {iconToolbar ? <Bars3BottomLeftIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
              <span className={iconToolbar ? "sr-only" : "text-xs"}>{t("Tittel")}</span>
            </button>

            {iconToolbar && <button
              type="button"
              onClick={() => setInsertMentionRequest((request) => request + 1)}
              disabled={disabled || isSubmitting || alreadyPublished}
              className="pd-composer-tool"
              title={t("dashboardDesign.mentionEmployee")}
              aria-label={t("dashboardDesign.mentionEmployee")}
            >
              <span aria-hidden="true" className="text-lg font-semibold leading-none">@</span>
            </button>}

            {iconToolbar && toolbarSlot}

            <DocumentUploadSectionV4
              iconOnly
              compact
              multiple
              module="status_update"
              moduleParam="new"
              module_param="new"
              disabled={disabled || isSubmitting || alreadyPublished}
              onFilesStaged={uploadQueue ? stageAttachments : undefined}
              parentCallback={handleUploadCallback}
            />

            <VoiceTranscriptButton
              onTranscript={appendTranscript}
              disabled={disabled}
              autoStart={autoDictate}
              onAutoStartConsumed={() => setAutoDictate(false)}
              suggestContext={suggestionContext}
            />

            {!iconToolbar && toolbarSlot}

            {!iconToolbar && visibilityChoices}
          </div>

          <div className={iconToolbar ? "pd-status-publish" : "flex flex-wrap items-center gap-2"}>
            {iconToolbar && visibilityChoices}
            {alreadyPublished && (
              <button
                type="button"
                onClick={resetComposerFields}
                className="rounded-xl px-3 py-2 text-sm font-medium text-[#1463C7] hover:bg-slate-100"
              >
                {t("oppdatering.new_draft", { defaultValue: "Ny oppdatering" })}
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              aria-label={isSubmitting ? t("Sender…") : alreadyPublished ? t("Publisert") : t("Publiser")}
              title={
                !canSubmit
                  ? alreadyPublished
                    ? t("oppdatering.already_published", {
                        defaultValue: "Allerede publisert — start en ny oppdatering",
                      })
                    : !hasContent
                      ? t("Skriv litt tekst eller legg til vedlegg for å publisere")
                      : isSubmitting
                        ? t("Sender…")
                        : undefined
                  : undefined
              }
              className={TWclassNames(
                iconToolbar ? "pd-publish-button" : "flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-all",
                !iconToolbar && canSubmit
                  ? "bg-[linear-gradient(135deg,#1463C7,#2D8BE8)] text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.6)] hover:brightness-105"
                  : !iconToolbar && "cursor-not-allowed bg-slate-100 text-slate-400"
              )}
            >
              <PaperAirplaneIcon className="h-5 w-5" />
              {iconToolbar && !isSubmitting && !alreadyPublished ? <span className="sr-only">{t("Publiser")}</span> : isSubmitting
                ? t("Sender…")
                : alreadyPublished
                  ? t("Publisert")
                  : t("Publiser")}
            </button>
          </div>
        </div>
      </div>

      {iconToolbar && <p className="pd-visibility-note">{t("dashboardDesign.visibleTo", { audience: t(VISIBILITY_OPTIONS.find((option) => option.value === visibility)?.labelKey || "Alle med tilgang") })}</p>}

      {(suggestedTodos.length > 0 || createdSuggestions.length > 0) && (
        <SuggestedTodosConfirm
          todos={suggestedTodos}
          createdTodos={createdSuggestions}
          statusUpdateId={lastCreatedId}
          projectId={
            subjectType === "project" ? subjectId : projectId || null
          }
          customerId={
            subjectType === "customer" ? subjectId : customer?.id || null
          }
          onDismiss={handleSuggestedTodosDismiss}
          onCreated={handleSuggestedTodosCreated}
        />
      )}

      {(suggestedHours.length > 0 || createdHourSuggestions.length > 0) && (
        <SuggestedHoursConfirm
          hours={suggestedHours}
          createdHours={createdHourSuggestions}
          defaultProjectId={
            subjectType === "project"
              ? subjectId
              : projectId || project?.id || null
          }
          defaultProjectName={
            project?.displayTitle || project?.name || ""
          }
          defaultActivityId={
            subjectType === "project_activity" ? subjectId : null
          }
          onDismiss={handleSuggestedHoursDismiss}
          onCreated={handleSuggestedHoursCreated}
        />
      )}
    </div>
  );
}

export default StatusUpdateComposer;
